import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Book, Zap, Folders, Share2, Code } from 'lucide-react';

function Documentation() {
  const navigate = useNavigate();

  const docSections = [
    {
      icon: Zap,
      title: 'Getting Started',
      items: [
        { title: 'Create Your First Prompt', desc: 'Learn how to build variable-driven prompt templates' },
        { title: 'Understanding Variables', desc: 'Use {{variable_name}} syntax to create dynamic prompts' },
        { title: 'Credits System', desc: 'How credits work and when they\'re used' },
        { title: 'Saving Presets', desc: 'Save frequently-used variable combinations' }
      ]
    },
    {
      icon: Folders,
      title: 'Organization',
      items: [
        { title: 'Projects & Folders', desc: 'Organize prompts into hierarchical structures' },
        { title: 'Visibility Settings', desc: 'Control who can see your prompts (private/public/shared)' },
        { title: 'Searching & Filtering', desc: 'Find prompts quickly with advanced filters' },
        { title: 'Tags & Categories', desc: 'Categorize prompts for better discovery' }
      ]
    },
    {
      icon: Share2,
      title: 'Sharing & Collaboration',
      items: [
        { title: 'Public Prompts', desc: 'Share prompts with the community' },
        { title: 'Forking Prompts', desc: 'Create copies of public prompts to customize' },
        { title: 'Whitelist Sharing', desc: 'Share specific prompts with select users' },
        { title: 'Permissions', desc: 'Set view-only or edit access for collaborators' }
      ]
    },
    {
      icon: Code,
      title: 'AI Features',
      items: [
        { title: 'RebelAI Generation', desc: 'Use AI to generate prompt templates (10 credits)' },
        { title: 'Prompt Improvement', desc: 'Enhance existing prompts with AI suggestions' },
        { title: 'Image Generation', desc: 'Create AI-generated images for prompts (20 credits)' },
        { title: 'Best Practices', desc: 'Tips for getting the best AI results' }
      ]
    }
  ];

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="card-glass mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl glass-strong flex items-center justify-center glow-blue">
              <Book className="w-8 h-8 text-rebel-blue" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-gradient">Documentation</h1>
              <p className="text-gray-400 mt-1">Everything you need to master Rebel Prompts</p>
            </div>
          </div>
        </div>

        {/* Quick Start */}
        <div className="card-glass mb-12">
          <h2 className="text-3xl font-bold mb-6">Quick Start Guide</h2>
          
          <div className="space-y-6">
            <div className="p-6 rounded-xl bg-white/5 border border-white/10">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-rebel-blue flex items-center justify-center text-sm font-bold">1</span>
                Create Your Account
              </h3>
              <p className="text-gray-300 ml-10">
                Sign up with your email and password, then complete your profile and start building prompts.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-white/5 border border-white/10">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-rebel-blue flex items-center justify-center text-sm font-bold">2</span>
                Build Your First Prompt
              </h3>
              <p className="text-gray-300 ml-10">
                Click "Create Prompt" from your dashboard. Write your prompt template using {'{{variable_name}}'} for dynamic parts.
                Variables are automatically detected and you can configure their input types.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-white/5 border border-white/10">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-rebel-blue flex items-center justify-center text-sm font-bold">3</span>
                Organize & Share
              </h3>
              <p className="text-gray-300 ml-10">
                Create projects and folders to organize your prompts. Set visibility to public to share with the community,
                or use whitelist sharing for specific collaborators.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-white/5 border border-white/10">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-rebel-blue flex items-center justify-center text-sm font-bold">4</span>
                Use AI Features
              </h3>
              <p className="text-gray-300 ml-10">
                Generate prompts with RebelAI (10 credits) or create AI images for your prompts (20 credits). 
                Manual prompt creation is always free and unlimited.
              </p>
            </div>
          </div>
        </div>

        {/* Documentation Sections */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {docSections.map((section, i) => (
            <div key={i} className="card-glass">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl glass-strong flex items-center justify-center">
                  <section.icon className="w-6 h-6 text-rebel-blue" />
                </div>
                <h2 className="text-2xl font-bold">{section.title}</h2>
              </div>
              <div className="space-y-4">
                {section.items.map((item, idx) => (
                  <div key={idx} className="pb-4 border-b border-white/5 last:border-0 last:pb-0">
                    <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="card-glass">
          <h2 className="text-3xl font-bold mb-8">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-2">What are credits and when are they used?</h3>
              <p className="text-gray-300">
                Credits are used only for AI-powered features like generating prompts with RebelAI (10 credits) or creating 
                AI images (20 credits). Creating, editing, organizing, and sharing prompts manually is completely free and unlimited.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">How do variables work in prompts?</h3>
              <p className="text-gray-300">
                Use curly braces around any word to create a variable: {'{topic}'}, {'{audience}'}, {'{tone}'}. 
                Rebel Prompts automatically detects these and lets you configure input types (text, dropdown, textarea, etc.).
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Can I share prompts with my team?</h3>
              <p className="text-gray-300">
                Yes! You can share prompts with specific users via email or username. Set view-only or edit permissions 
                for each collaborator. Pro subscribers get team collaboration features.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">What happens if I run out of credits?</h3>
              <p className="text-gray-300">
                You can purchase additional credits anytime. Your subscription renews monthly credits automatically. 
                You can still use all manual features without credits.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-2">How does forking work?</h3>
              <p className="text-gray-300">
                Forking creates a copy of a public prompt in your workspace. You can then customize it for your needs. 
                The original creator's prompt remains unchanged. Forking is always free.
              </p>
            </div>
          </div>
        </div>

        {/* Need Help */}
        <div className="mt-12 text-center card-glass">
          <h2 className="text-2xl font-bold mb-4">Need More Help?</h2>
          <p className="text-gray-400 mb-6">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <button
            onClick={() => navigate('/support')}
            className="btn-primary"
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
}

export default Documentation;
